import React from "react";
import * as RegionSelector from "../RegionSelector_v2";
import { Spacer } from "../Spacer";
import { maybe } from "../utils/Maybe";
import { CreatePageBlock } from "./CreatePageBlock";
import { CreatePageRegionSelectorState } from "./CreatePageRegionSelectorState";

type CreatePageRegionSelectorProps = {
  /**
   * The state of the region selector. See `CreatePage.createRegionSelectorState` for more details.
   */
  state: CreatePageRegionSelectorState;
};

const renderResourceLocationSelector = (state: CreatePageRegionSelectorState["resourceLocations"]) => {
  switch (state.status) {
    case "loading":
      return <RegionSelector.ResourceLocationSkeleton count={3} />;
    case "error":
      return <RegionSelector.ResourceLocationSkeleton count={3} />;
    case "ready": {
      if (maybe.is.none(state.data)) {
        return null;
      }

      const { name, onSelect, selected, options } = state.data.value;

      const userHasResources = options.some((option) => option.resources.length > 0);

      if (userHasResources) {
        return <RegionSelector.ResourceLocationRadioGroup name={name} selected={selected} onSelect={onSelect} options={options} />;
      } else {
        return <RegionSelector.ResourceLocationSelectMenu name={name} selected={selected} onSelect={onSelect} options={options} />;
      }
    }
  }
};

const renderVpcSelector = (state: CreatePageRegionSelectorState["vpcs"]) => {
  switch (state.status) {
    case "loading":
      return <RegionSelector.VPCSkeleton />;
    case "error":
      return <RegionSelector.VPCSkeleton />;
    case "ready": {
      if (maybe.is.none(state.data)) {
        return null;
      }

      const { name, onSelect, options, selected } = state.data.value;

      switch (options.length) {
        case 1:
          return <RegionSelector.VPCSingleValue name={name} selected={selected as string} />;
        case 2:
        case 3:
        case 4:
          return <RegionSelector.VPCRadioGroup name={name} selected={selected as string} onSelect={onSelect} options={options} />;
        default:
          return <RegionSelector.VPCSelectMenu name={name} selected={selected as string} onSelect={onSelect} options={options} />;
      }
    }
  }
};

export const CreatePageRegionSelector: React.FC<CreatePageRegionSelectorProps> = React.memo((props) => {
  const resourceLocations = renderResourceLocationSelector(props.state.resourceLocations);
  const vpcs = renderVpcSelector(props.state.vpcs);

  const spacer = React.useMemo(() => {
    /**
     * No need for a spacer if either the resourceLocations or vpcs render nothing.
     */
    if (resourceLocations === null || vpcs === null) {
      return null;
    }

    return <Spacer space={7} />;
  }, [resourceLocations === null, vpcs === null]);

  return (
    <CreatePageBlock>
      {resourceLocations}
      {spacer}
      {vpcs}
    </CreatePageBlock>
  );
});

CreatePageRegionSelector.displayName = "CreatePageRegionSelector";
